import { z } from 'zod';
import { BankAccountTypeSchema } from './bank-account.js';
// One per taxpayer (parent). The Vendor row is the transactable BRANCH (child).
// Backward-compatible: existing vendors are backfilled under auto-created suppliers.
export const SupplierSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    code: z.string().min(1).max(32), // 'S-00001'
    // 13-digit Thai tax IDs in practice; nullable for foreign/non-VAT taxpayers.
    taxId: z.string().nullable().optional(),
    nameTh: z.string().min(1),
    // Parent-level defaults cascaded to branches unless a branch overrides.
    defaultExpenseCode: z.string().nullable().optional(),
    defaultDepartmentId: z.uuid().nullable().optional(),
    vatClaimRequiresOriginal: z.boolean().optional(),
    // WHT INTENT defaults (cert is issued at payment — never here).
    defaultWhtIncomeType: z.string().nullable().optional(),
    defaultWhtRate: z.coerce.number().nullable().optional(),
    terms: z.string().nullable().optional(),
    currency: z
        .string()
        .length(3)
        .regex(/^[A-Z]{3}$/),
    country: z.string().min(2).max(2),
    isActive: z.boolean(),
    // RD VAT-registration snapshot (per-taxpayer).
    rdVerdict: z.string().nullable().optional(),
    rdVerifiedAt: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const CreateSupplierSchema = SupplierSchema.omit({
    id: true,
    rdVerdict: true,
    rdVerifiedAt: true,
    createdAt: true,
    updatedAt: true,
});
export const UpdateSupplierSchema = CreateSupplierSchema.partial().omit({
    companyId: true,
});
// The BRANCH (= Vendor row). branchCode defaults to head office 00000.
export const SupplierBranchSchema = z.object({
    id: z.uuid(),
    supplierId: z.uuid(),
    companyId: z.uuid(),
    code: z.string().min(1).max(32), // 'V-00001'
    nameTh: z.string().min(1),
    taxId: z.string().nullable().optional(),
    branchCode: z.string().default('00000'),
    branchName: z.string().nullable().optional(),
    addressTh: z.string().nullable().optional(),
    addressJson: z.unknown().nullable().optional(),
    defaultExpenseCode: z.string().nullable().optional(),
    defaultDepartmentId: z.uuid().nullable().optional(),
    vatClaimRequiresOriginal: z.boolean().optional(),
    terms: z.string().nullable().optional(),
    email: z.email().nullable().optional(),
    currency: z
        .string()
        .length(3)
        .regex(/^[A-Z]{3}$/),
    country: z.string().min(2).max(2),
    isActive: z.boolean(),
    // M7 Phase 2 (D1) — single destination bank account stored as COLUMNS on the
    // Vendor row. READ-ONLY here: a change goes through the VBC 4-eyes routing gate
    // (…/bank-change), never a direct PATCH on this branch. Optional so existing
    // read shapes stay backward-compatible.
    bankAccountNumber: z.string().nullable().optional(),
    bankCode: z.string().nullable().optional(),
    bankBranchCode: z.string().nullable().optional(),
    accountHolderName: z.string().nullable().optional(),
    bankAccountType: BankAccountTypeSchema.nullable().optional(),
});
// Branch create body: supplierId/companyId are supplied by the route, omitted here.
// NOTE: named CreateSupplierBranch* (not CreateBranch*) because the existing
// company-location `branch.ts` master already exports CreateBranch/CreateBranchSchema/
// UpdateBranch/UpdateBranchSchema app-wide; reusing those names would collide in the
// shared barrel (TS2308). The supplier-branch (= Vendor row) variants are distinct.
export const CreateSupplierBranchSchema = SupplierBranchSchema.omit({
    id: true,
    supplierId: true,
    companyId: true,
    code: true,
    // M7 Phase 2 (D2) — the bank columns are READ-ONLY on create/update. A bank
    // change MUST route through the VBC 4-eyes gate (…/bank-change); dropping them
    // here keeps the create/update branch body from carrying a direct bank edit.
    bankAccountNumber: true,
    bankCode: true,
    bankBranchCode: true,
    accountHolderName: true,
    bankAccountType: true,
}).extend({
    // Vendor code is auto-generated server-side (nextVendorCode) when omitted, so
    // it is OPTIONAL on the create body — sending '' / undefined triggers auto-gen.
    code: z.string().max(32).optional(),
    branchCode: z.string().default('00000'),
});
export const UpdateSupplierBranchSchema = CreateSupplierBranchSchema.partial();
// POST /companies/:companyId/suppliers — entity + first branch atomically.
// companyId is the URL param (NOT the body) and `code` is auto-generated
// (nextSupplierCode) when omitted, so both are dropped from the create body —
// this mirrors SuppliersService.createWithBranch's real contract.
export const CreateSupplierWithBranchSchema = CreateSupplierSchema.omit({
    companyId: true,
    code: true,
}).extend({
    code: z.string().max(32).optional(),
    branch: CreateSupplierBranchSchema,
});
// Aggregated master-list row (one per Supplier).
export const SupplierListRowSchema = z.object({
    id: z.uuid(),
    code: z.string(),
    taxId: z.string().nullable(),
    nameTh: z.string(),
    branchCount: z.number().int().nonnegative(),
    rdVerdict: z.string().nullable(),
    isActive: z.boolean(),
});
// Contacts sub-table (chasing original documents).
export const SupplierContactSchema = z.object({
    id: z.uuid(),
    supplierId: z.uuid(),
    name: z.string().min(1),
    role: z.string().nullable().optional(),
    email: z.email().nullable().optional(),
    phone: z.string().nullable().optional(),
    isPrimary: z.boolean(),
});
export const CreateSupplierContactSchema = SupplierContactSchema.omit({
    id: true,
    supplierId: true,
}).extend({ isPrimary: z.boolean().default(false) });
// WHT INTENT — captured on the bill; the certificate is issued at PAYMENT.
export const WhtIntentSchema = z.object({
    whtIncomeType: z.string().optional(),
    whtRate: z.coerce.number().optional(),
    whtBase: z.coerce.number().optional(),
});
// Supplier-360 read aggregate (GET /suppliers/:id/overview).
export const Supplier360Schema = z.object({
    supplier: SupplierSchema,
    branches: z.array(SupplierBranchSchema),
    openApBalance: z.number(),
    agingBuckets: z.object({
        current: z.number(),
        d1to30: z.number(),
        d31to60: z.number(),
        d61to90: z.number(),
        over90: z.number(),
    }),
    lastPaidAt: z.coerce.date().nullable(),
    ytdSpend: z.number(),
    recentIngests: z.array(z.object({
        ingestId: z.string(),
        fileName: z.string(),
        status: z.string(),
        createdAt: z.coerce.date(),
    })),
});
// POST /suppliers/bulk-reverify — loop single-flight verify over selected ids.
export const BulkReverifyRequestSchema = z.object({
    supplierIds: z.array(z.uuid()).min(1).max(200),
});
export const BulkReverifyResultSchema = z.object({
    results: z.array(z.object({
        supplierId: z.uuid(),
        verdict: z.string(),
        matchScore: z.number().nullable(),
    })),
});
// ── Verification dashboard buckets (M2-d §15.2) ───────────────────────────
// Freshness-aware grouping for the verification dashboard. DISTINCT from the
// raw RdVerdict enum (rd-verify.ts): EXPIRED = verified but stale, UNVERIFIED =
// never checked. The dashboard derives a bucket from rdVerdict + rdVerifiedAt.
export const VERIFICATION_BUCKETS = [
    'VERIFIED',
    'NAME_MISMATCH',
    'BRANCH_MISMATCH',
    'NOT_VAT_REGISTERED',
    'EXPIRED',
    'UNVERIFIED',
];
//# sourceMappingURL=supplier.js.map